// Angular imports
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnInit } from '@angular/core';

// 3rd party imports
import * as Sentry from '@sentry/angular';
import notify from 'devextreme/ui/notify';

// Rxjs import,
import { Subject } from 'rxjs';

// CC-Common imports
import { Utils } from 'cc-common';

// Service imports
import { AuthService } from '../auth.service';
import { UserService } from '../data-services/user.service';

// Constant imports
import { MessageInterval } from '../../_constants/messages-intervals';

// Model imports
import { MessageBoxInfo } from '../../_models/message-box-info';

// environment imports
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class ServerErrorService implements OnInit {
    private readonly _subjectErrorMessageChanged: Subject<string> = new Subject();
    private readonly _subjectMessageChanged: Subject<MessageBoxInfo> = new Subject();
    private _hasRegisteredUnhandledRejection = false;

    constructor(private readonly authService: AuthService, private readonly userService: UserService) {}

    ngOnInit(): void {
        if (!this._hasRegisteredUnhandledRejection) {
            window.addEventListener('unhandledrejection', this.handleRejection);
            this._hasRegisteredUnhandledRejection = true;
        }
    }

    // Need to change defaultErrorMsgInterval
    displayError(error: HttpErrorResponse, prefixMessage = '', displayTime = MessageInterval.defaultErrorMsgInterval) {
        if (prefixMessage !== '') {
            prefixMessage += '. ';
        }

        if (!navigator.onLine) {
            // browser offline
            this.displayMessage('No internet connection. Please check and try again', 'error');
        } else if (error.status === 400) {
            // bad request
            this.process400Error(error);
        } else if (error.status === 401) {
            // unauthorized
            this.process401Error(error);
        } else if (error.status === 403) {
            // forbidden
            this.process403Error(error, prefixMessage);
        } else if (error.status === 404) {
            // not found
            this.process404Error(error, prefixMessage);
        } else if (error.status === 409) {
            this.process409Error(error, prefixMessage);
        } else if (error.status === 500) {
            this.process500Error(error, prefixMessage);
        } else if (error.status === 503) {
            // Redirecting to offline server, if API server is unavailable.
            window.location.href = environment.offlineRedirectUrl;
        } else {
            this.processOtherError(error, prefixMessage);
        }
    }

    displayInfo(info: string, displayTime: number = MessageInterval.defaultErrorMsgInterval) {
        // javascript error only
        notify(info, 'info', displayTime);
    }

    displayMessage(message: string, type: string, displayTime = MessageInterval.defaultErrorMsgInterval) {
        // javascript error only
        notify(message, type, displayTime);
    }

    private handleRejection = (event: PromiseRejectionEvent): void => {
        const reason = event.reason;
        if (this.isTrustedError(reason)) {
            this.captureUnhandledRejection(reason);
        } else {
            console.warn('Unhandled Rejection Detected');
        }
    };

    private getErrorMessage(error: HttpErrorResponse, defaultMessage: string): string {
        let message: any = '';
        if (!!error && !!error.error) {
            message = error.error;
            if (typeof message !== 'string') {
                if (!!message.byteLength) {
                    const enc = new TextDecoder('utf-8');
                    const arr = new Uint8Array(error.error);
                    const msg = enc.decode(arr);
                    try {
                        const json = JSON.parse(msg);
                        message = json.message || json.error;
                    } catch {
                        message = msg;
                    }
                } else {
                    message = '';
                }
            }
        }
        return !!message.length ? message : defaultMessage;
    }

    private process400Error(error: HttpErrorResponse): void {
        let errors = [];
        let modelState: any;
        if (!!error.error) {
            if (error.error.hasOwnProperty('modelState')) {
                modelState = error.error.modelState;
            } else if (error.error.hasOwnProperty('ModelState')) {
                modelState = error.error.ModelState;
            }
            if (!!modelState) {
                for (const property in modelState) {
                    if (modelState.hasOwnProperty(property)) {
                        errors = errors.concat(modelState[property]);
                    }
                }
            } else if (!!error.error.message) {
                errors.push(error.error.message);
            } else if (!!error.error.byteLength) {
                const enc = new TextDecoder('utf-8');
                const arr = new Uint8Array(error.error);
                const msg = enc.decode(arr);
                try {
                    const json = JSON.parse(msg);
                    error.error.message = json.message || json.error;
                } catch {
                    error.error.message = msg;
                }
                errors.push(error.error.message);
            } else {
                errors.push(error.error);
            }
        }
        this.displayMessage(errors.join(), 'error');
    }

    private process401Error(error: HttpErrorResponse): void {
        const message = this.getErrorMessage(error, 'Application session is expired! Please login to continue.');
        this.updateSubjectErrorMessage(message);
        this.authService.logOut();
    }

    private process403Error(error: HttpErrorResponse, suffixMessage: string): void {
        const message = this.getErrorMessage(error, `Access Forbidden. `);
        this.updateSubjectErrorMessage(message + suffixMessage);
    }

    private process404Error(error: HttpErrorResponse, suffixMessage: string): void {
        const message = this.getErrorMessage(error, `Resource Not Found. `);
        this.updateSubjectErrorMessage(message + suffixMessage);
    }

    private process409Error(error: HttpErrorResponse, suffixMessage: string): void {
        const message = this.getErrorMessage(error, `Server Data Conflict. `);
        let customMessage = `${message}${suffixMessage}`;
        if (!!error && !!error.error) {
            const errorMessage = String.fromCharCode.apply(null, new Uint8Array(error.error));
            if (!!errorMessage) {
                customMessage = errorMessage;
            }
        }
        this.updateSubjectErrorMessage(customMessage);
    }

    private process500Error(error: HttpErrorResponse, suffixMessage: string): void {
        const message = this.getErrorMessage(error, `Internal server error. `);
        this.updateSubjectErrorMessage(message + suffixMessage);
    }

    private processOtherError(error: HttpErrorResponse, prefixMessage: string): void {
        const message = this.getErrorMessage(error, `Error occurred. `);
        this.displayMessage(prefixMessage + message, 'error');
    }

    updateSubjectErrorMessage(message: string): void {
        this._subjectErrorMessageChanged.next(message);
    }

    getSubjectErrorMessage(): Subject<string> {
        return this._subjectErrorMessageChanged;
    }

    updateSubjectMessage(info: MessageBoxInfo): void {
        this._subjectMessageChanged.next(info);
    }

    getSubjectMessage(): Subject<MessageBoxInfo> {
        return this._subjectMessageChanged;
    }

    private captureToSentry(message: string): void {
        // Check if message is null or invalid
        if (Utils.isNullOrEmpty(message)) {
            return;
        }

        // Set user context in Sentry
        let sentryUser: any = null;
        if (this.authService.isLoggedIn) {
            const user = this.userService.getCurrentUserInfo();
            sentryUser = {
                id: user.userId,
                username: user.userId,
                email: user.email
            };
        }
        Sentry.setUser(sentryUser);

        // Capture new exception with custom message
        Sentry.captureException(new Error(message));
    }

    private captureUnhandledRejection(reason: any): void {
        if (Utils.isNullOrEmpty(reason)) {
            return;
        }

        // Set user context in Sentry
        if (this.authService.isLoggedIn) {
            const user = this.userService.getCurrentUserInfo();
            if (Utils.notNullAndDefined(user) && Utils.notNullOrEmpty(user.userId)) {
                Sentry.setUser({
                    id: user.userId,
                    username: user.userId,
                    email: user.email
                });
            }
        }

        // Add a breadcrumb with the rejection details (including full object data if present)
        Sentry.addBreadcrumb({
            category: 'unhandled-rejection',
            message: 'Unhandled Promise Rejection',
            level: Sentry.Severity.Error,
            data: {
                rejectionReason: reason,
                rejectionType: typeof reason,
                isError: reason instanceof Error
            }
        });

        if (reason instanceof Error) {
            // If reason is an Error, capture it directly with breadcrumbs
            Sentry.captureException(reason);
        } else {
            // Create a concise error message
            let errorMessage = 'Unhandled promise rejection';

            // Append the reason to the message if it is a plain string
            // otherwise, append the reason type (likely 'object')
            if (reason !== null && reason !== undefined) {
                if (typeof reason === 'string') {
                    // Only append the reason if it's a string
                    errorMessage += `: ${reason}`;
                } else {
                    // For objects, just indicate the type
                    errorMessage += ` (${typeof reason})`;
                }
            }

            // Create new Error containing errorMessage and breadcrumbs
            Sentry.captureException(new Error(errorMessage));
        }
    }

    // Filtering known/untrusted errors
    private isTrustedError(error: any): boolean {
        const trustedDomains = [
            environment.ccApiUrl,
            environment.userActivityHostApiUrl,
            environment.portalSelectionUrl,
            environment.boardMemberRedirectionUrl
        ]; // Add your trusted domains
        if (error && typeof error === 'object' && 'stack' in error && typeof error.stack === 'string') {
            return trustedDomains.some((domain) => error.stack.includes(domain));
        }
        return false;
    }
}
